import Notify from 'quasar/src/plugins/Notify.js';;
import $http, { genericHttp } from '../../shared/http';
import i18next from 'i18next';
import Vue from 'vue';
import { emitter } from '../../event_bus';
import store from '../index.js';

const churchId = () => store.getters['auth/inStoreResolvedOrgId']();
const inDevelopment = () => !!store.state.inDevelopment;

export const payments = {
  namespaced: true,
  state: () => {
    return {
      publicKey: '',
      paymentMode: '',
      apiProvider: '',
      checkoutDetails: {
        tx_ref: '',
        amount: 0,
        currency: '',
        country: '',
        purpose: '',
        payment_options: 'card,mobilemoney,ussd',
        customer: {
          email: '',
          phone_number: '',
          name: '',
        },
      },
      customisations: {
        title: '',
        description: '',
        logo: '',
      },
    };
  },
  getters: {
    getCheckoutDetails: (state) => state.checkoutDetails,
    getCustomisationDetails: (state) => state.customisations,
    getApiProvider: (state) => state.apiProvider,
    getPaymentMode: (state) => state.paymentMode,
    getPublicKeys: (state) => state.publicKeys,
    getFlutterwaveConfig: (state, getters) => {
      return {
        public_key: state.publicKey,
        tx_ref: getters['getCheckoutDetails'].tx_ref,
        amount: getters['getCheckoutDetails'].amount,
        currency: getters['getCheckoutDetails'].currency,
        country: getters['getCheckoutDetails'].country,
        payment_options: getters['getCheckoutDetails'].payment_options,
        customer: {
          email: getters['getCheckoutDetails'].customer.email,
          phone_number: getters['getCheckoutDetails'].customer.phone_number,
          name: getters['getCheckoutDetails'].customer.name,
        },
        customizations: getters['getCustomisationDetails'],
      };
    },
  },
  mutations: {
    setCustomerDetails: (state, payload) => {
      state.checkoutDetails.customer = payload;
    },
    setCustomisationDetails: (state, payload) => {
      state.customisations.title = payload.title;
      state.customisations.logo = payload.logo;
    },
    setCurrencyDetails: (state, payload) => {
      state.checkoutDetails.country = payload.country;
      state.checkoutDetails.currency = payload.currency;
    },
    setTransactionDetails: (state, payload) => {
      state.checkoutDetails.tx_ref = payload.tx_ref;
      state.checkoutDetails.amount = payload.amount;
      state.checkoutDetails.purpose = payload.purpose;
    },
    setCustomisations: (state, payload) => {
      state.customisations = payload;
    },
    setCredentials: (state, payload) => {
      const { providerPublicKey, providerMode, providerName } = payload;
      state.publicKey = providerPublicKey;
      state.paymentMode = providerMode;
      state.apiProvider = providerName;
    },

    nullCredentials: (state) => {
      nullCredentialsFn(state);
    },
  },
  actions: {
    setPaymentDetailsAction({ commit, rootGetters }) {
      const customisations = {
        title: rootGetters['getAppName'],
        logo: rootGetters['getDefaultLogo'],
      };

      commit('setCustomisationDetails', customisations);
    },

    MAKE_PAYMENT_ACTION({ getters }) {
      if (getters['getApiProvider'] === 'flutterwave') {
        // eslint-disable-next-line no-undef
        FlutterwaveCheckout({
          ...getters['getFlutterwaveConfig'],
          callback: async function (data) {
            await paymentCallback(data);
          },
        });

        return;
      }

      throw new Error('Unsupported payment provider');
    },

    async INIT_PAYMENT_ACTION({ commit }, { payload, churchId }) {
      return $http('v2')
        .post(`organisations/${churchId}/payments`, {
          amount: payload.amount,
          purpose: payload.purpose,
        })
        .then((response) => {
          return handlePaymentInitialisationResponse(response, commit);
        })
        .catch((error) => {
          return Promise.reject(error?.response ?? error);
        });
    },

    async VERIFY_TRANSACTION_ACTION({ dispatch, getters }, payload) {
      const {
        churchId,
        gotedo_trx_ref: gotedoTrxRef,
        amount,
        charged_amount,
        currency,
        purpose,
        pmt_provider_ref,
        pmt_provider_trx_id,
      } = payload;

      return $http('v2')
        .post(
          `organisations/${churchId}/payments/${gotedoTrxRef}`,
          {
            provider: getters.getApiProvider,
            amount,
            charged_amount,
            currency,
            purpose,
            pmt_provider_ref,
            pmt_provider_trx_id,
          },
          { headers: { 'X-Is-Notifiable': 'On' } }
        )
        .then((response) => {
          dispatch('sms/LOAD_SMS_ACCOUNT', { churchId }, { root: true });
          return response;
        })
        .catch((error) => {
          return Promise.reject(error?.response ?? error);
        });
    },

    async installPaymentProviderScript(_, provider) {
      switch (provider) {
        case 'flutterwave':
          {
            const paymentScript = document.createElement('script');
            paymentScript.setAttribute('src', 'https://checkout.flutterwave.com/v3.js');
            document.head.appendChild(paymentScript);
          }
          break;

        default:
          throw new Error('Unknown payment provider');
      }
    },

    async initiateProductSubscriptionPayment({ commit }, { url, method, data }) {
      return genericHttp({ url, method, data }).then((response) => {
        return handlePaymentInitialisationResponse(response, commit);
      });
    },
  },
};

const nullCredentialsFn = function (state) {
  state.paymentMode = '';
  state.apiProvider = '';
  for (const provider in state.publicKeys) {
    if (Object.hasOwnProperty.call(state.publicKeys, provider)) {
      state.publicKeys[provider].test = '';
      state.publicKeys[provider].live = '';
    }
  }
};

function handlePaymentInitialisationResponse(response, commit) {
  const customer = {
    name: response.data.data.customer_name,
    email: response.data.data.customer_email,
    phone_number: response.data.data.customer_phone_number,
  };

  const currency = {
    currency: response.data.data.currency.code,
    country: response.data.data.country_code,
  };

  commit('setCustomerDetails', customer);
  commit('setCurrencyDetails', currency);
  commit('setCustomisations', response.data.data.customisations);

  const returnedAmount = Number(response.data.data.amount);
  const currencyExponent = Number(response.data.data.currency.exponent);
  const amount =
    currencyExponent === 0 ? returnedAmount : returnedAmount / Math.pow(10, currencyExponent);

  commit('setTransactionDetails', {
    tx_ref: response.data.data.gotedo_trx_ref,
    amount,
    purpose: response.data.data.purpose_name,
  });

  commit('setCredentials', response.data.data.credentials);

  return response.data.data;
}

function paymentCallback(data) {
  if (inDevelopment) {
    console.log('callback running: ', data);
  }
  const {
    amount,
    charged_amount,
    currency,
    tx_ref: gotedo_trx_ref,
    flw_ref: pmt_provider_ref,
    transaction_id: pmt_provider_trx_id,
  } = data;

  Vue.gtm.trackEvent({
    event: 'GotedoCustomEvent',
    category: 'SMS Events',
    action: 'Verify Payment',
    label: 'Verify Payment',
    value: amount,
    noninteraction: false,
  });

  if (inDevelopment) {
    console.log({
      amount: amount.toString(),
      charged_amount: charged_amount.toString(),
      currency,
      gotedo_trx_ref: gotedo_trx_ref.toString(),
      pmt_provider_ref: pmt_provider_ref.toString(),
      pmt_provider_trx_id: pmt_provider_trx_id.toString(),
    });
  }

  Notify.create({
    type: 'positive',
    message: i18next.t('payment_was_successful', { ns: 'toasts' }),
    position: 'top',
    timeout: 0,
  });

  // Emit event for navigation
  emitter.emit('navigate', { name: 'billing', params: { churchId } });
}
